"use client";

import { c as _c } from "react/compiler-runtime";
import { AgentActivityKind } from "@/components/ai-chat/agent-activity-log";
import { AgentActivityLog, type AgentActivityLogItem } from "@/components/ai-chat/agent-activity-log";
import { AgentExecutionStatus } from "@/types/agent-execution";
import type { AnalyticsStep } from "@/types/analytics-ai";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatSql(sql: string): string {
  const keywords = ["UNION ALL", "LEFT JOIN", "RIGHT JOIN", "INNER JOIN", "OUTER JOIN", "GROUP BY", "ORDER BY", "SELECT", "FROM", "WHERE", "JOIN", "ON", "AND", "OR", "LIMIT", "OFFSET", "HAVING", "UNION", "WITH"];
  let formatted = sql.trim();
  for (const kw of keywords) {
    const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    formatted = formatted.replace(new RegExp(`\\b${escaped}\\b`, "gi"), m => `\n${m.toUpperCase()}`);
  }
  return formatted.split("\n").map(l => l.trim()).filter(Boolean).map((l, i) => i === 0 ? l : "  " + l).join("\n");
}
const HIDDEN_STEPS = new Set(["complete"]);
const RENDERABLE_STEPS = new Set(["plan", "search_context", "retrying", "generating_sql", "executing", "analyzing", "schema", "scope_check", "set_visualization"]);
const RENDERABLE_PREFIXES = ["query_", "python_", "dashboard_"];

/** Should we display this step at all? */
function isRenderableStep(name: string): boolean {
  if (HIDDEN_STEPS.has(name)) return false;
  if (RENDERABLE_STEPS.has(name)) return true;
  return RENDERABLE_PREFIXES.some(prefix => name.startsWith(prefix));
}
function getStepKind(step: string): AgentActivityLogItem["kind"] {
  if (step === "plan") return AgentActivityKind.PLAN;
  if (step === "search_context") return AgentActivityKind.SEARCH;
  if (step === "retrying") return AgentActivityKind.RETRY;
  if (step === "analyzing" || step.startsWith("python_")) return AgentActivityKind.CODE;
  if (step === "set_visualization") return AgentActivityKind.CHART;
  return AgentActivityKind.DATABASE;
}
function getStepStatus(status: AnalyticsStep["status"]): AgentActivityLogItem["status"] {
  return status;
}
function toActivityItem({
  step,
  sql,
  pythonCode,
  searchContext
}: {
  step: AnalyticsStep;
  sql?: string | null;
  pythonCode?: string | null;
  searchContext?: string | null;
}): AgentActivityLogItem {
  const stepSql = step.sql ?? (step.step === "generating_sql" ? sql : undefined);
  const stepPython = step.python_code ?? (step.step === "analyzing" ? pythonCode : undefined);
  const details: AgentActivityLogItem["details"] = [];
  if (step.status === AgentExecutionStatus.COMPLETED && stepSql && (step.step.startsWith("query_") || step.step === "generating_sql" || step.step === "executing")) {
    details.push({
      label: "View SQL",
      content: formatSql(stepSql),
      language: "sql"
    });
  }
  if (step.status === AgentExecutionStatus.COMPLETED && stepPython && (step.step.startsWith("python_") || step.step === "analyzing")) {
    details.push({
      label: "View Python",
      content: stepPython,
      language: "python"
    });
  }
  if (step.status === AgentExecutionStatus.COMPLETED && step.step === "search_context" && searchContext) {
    details.push({
      label: "View Context",
      content: searchContext
    });
  }
  return {
    id: step.step,
    label: step.title ?? step.step,
    status: getStepStatus(step.status),
    kind: getStepKind(step.step),
    elapsedMs: step.elapsed_ms,
    details
  };
}
interface AgentPlanProps {
  steps: AnalyticsStep[];
  sql?: string | null;
  pythonCode?: string | null;
  searchContext?: string | null;
  defaultCollapsed?: boolean;
}
export function AgentPlan(t0) {
  const $ = _c(8);
  const {
    steps,
    sql,
    pythonCode,
    searchContext,
    defaultCollapsed: t1
  } = t0;
  const defaultCollapsed = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== pythonCode || $[1] !== searchContext || $[2] !== sql || $[3] !== steps) {
    t2 = Array.from(steps.filter(_temp).reduce(_temp2, new Map()).values()).map(step => toActivityItem({
      step,
      sql,
      pythonCode,
      searchContext
    }));
    $[0] = pythonCode;
    $[1] = searchContext;
    $[2] = sql;
    $[3] = steps;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const items = t2;
  let t3;
  if ($[5] !== defaultCollapsed || $[6] !== items) {
    t3 = <AgentActivityLog items={items} defaultCollapsed={defaultCollapsed} />;
    $[5] = defaultCollapsed;
    $[6] = items;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function _temp2(map, s_0) {
  return map.set(s_0.step, s_0);
}
function _temp(s) {
  return isRenderableStep(s.step);
}